import { useEffect, useState } from 'react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { useCurrentUser } from '@/features/auth/state/auth'
import { useFetchClientByCnpjCpf } from '@/features/client/services'

import { Edit2, Save, Lock, PlusIcon, Trash2 } from 'lucide-react'

import { useToast } from '@/lib/hooks/use-toast'
import { MaskInputDin } from '@/components/ui/mask-input-din'
import { MesAnoInput } from '@/components/form/input/date-mask-input'

import { FileManager } from '@/components/form/anexar'
import { useApuracaoSelecionada } from '../../hooks/seApuracaoSelecionada'
import { formatCurrency } from '../../utils'
import { useUpdateDarf } from '../../services/useUpdateDarfs'
import { Darf, useCreateDarf } from '../../services/useCreateDarfs'
import { useFetchDarfsByApuracaoCompetencia } from '../../services/useFindDarfsByIdApuracaoCompentica'
import { useDeleteDarf } from '../../services/useDeleteDarfs'


export default function DetalhesDeCredito({
  cnpj,
  id_product,
  onChange
}: {
  cnpj: string
  id_product: string
  onChange?: () => void
}) {
  const [editingRows, setEditingRows] = useState<Set<string>>(new Set())
  const { data: clientData } = useFetchClientByCnpjCpf(cnpj)

  const [isModalOpen, setIsModalOpen] = useState<{
    open: boolean
    id: string | null
  }>({
    open: false,
    id: null
  })

  const { apuracaoSelecionada } = useApuracaoSelecionada()

  const isEditable = apuracaoSelecionada?.isModifica === true
  const id_apuracao = apuracaoSelecionada?.id
  const competencias = apuracaoSelecionada?.competencias
  const competenciaSelecionada = Array.isArray(competencias) ? competencias[0] : competencias
  const id_apuracao_competencia = competenciaSelecionada?.id_apuracao_competencia ?? ''

  const user = useCurrentUser()
  const { toast } = useToast()
const { mutate: updateDarf } = useUpdateDarf()
const { mutate: createDarf } = useCreateDarf()
const { mutate: deleteDarf } = useDeleteDarf()
const { data: darfData = [] } = useFetchDarfsByApuracaoCompetencia(
  id_apuracao_competencia ?? '',
)
const [darfControls, setDarfControls] = useState<Darf[]>([])

  useEffect(() => {
    if (darfData) {
      setDarfControls(darfData)
    }
  }, [darfData])

  const handleToggleEdit = (id: string) => {
    setEditingRows(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

const handleDarfControlChange = (id: string, field: string, value: boolean | string | number) => {
  setDarfControls(prevControls =>
    prevControls.map(control => {
      if (control.id !== id) return control

      const newControl = { ...control }

      if (field === 'isPago' && typeof value === 'boolean') {
        newControl.isPago = value
      } else if (field === 'status' && typeof value === 'boolean') {
        newControl.status = value as Darf['status']
      } else if (field === 'competencia' && typeof value === 'string') {
        const cleaned = value.replace(/\D/g, '')
        newControl.mesCompetencia = cleaned.slice(0, 2)
        newControl.anoCompetencia = cleaned.slice(2, 6)
      } else if (field === 'value') {
        newControl.value = Number(value) || 0
      }

      return newControl
    })
  )

  onChange && onChange()
}
const handleSaveDarfControl = (darf: Darf) => {
  const normalizedApuracaoCompetencia =
    darf.id_apuracao_competencia?.trim() || id_apuracao_competencia.trim() || undefined
  const clientId = typeof clientData?.id === 'string' ? clientData.id : undefined

  if (!normalizedApuracaoCompetencia) {
    toast({ variant: 'destructive', description: 'Selecione uma apuração antes de salvar a DARF.' })
    return
  }

  if (!darf.mesCompetencia?.trim() || !darf.anoCompetencia?.trim()) {
    toast({ variant: 'destructive', description: 'Informe a competência da DARF.' })
    return
  }

  if (!Number(darf.value)) {
    toast({ variant: 'destructive', description: 'Informe o valor da DARF.' })
    return
  }

  const body = {
    anoCompetencia: darf.anoCompetencia,
    mesCompetencia: darf.mesCompetencia,
    value: Number(darf.value),
    isPago: darf.isPago,
    status: darf.status,
    id_apuracao_competencia: normalizedApuracaoCompetencia,
    ...(clientId ? { id_client: clientId } : {}),
  }

  if (darf.id.startsWith('temp-')) {
    createDarf(
      body,
      {
        onSuccess: data => {
          setDarfControls(prevControls => prevControls.map(control => (control.id === darf.id ? data : control)))
          toast({ variant: 'default', description: 'A DARF foi salva com sucesso!' })
          setEditingRows(prev => {
            const newSet = new Set(prev)
            newSet.delete(darf.id)
            return newSet
          })
        },
        onError: () => {
          toast({ variant: 'destructive', description: 'Houve um erro ao salvar a DARF.' })
        }
      }
    )
    return
  }

  updateDarf(
    { id: darf.id, body },
    {
      onSuccess: () => {
        toast({ variant: 'default', description: 'A DARF foi salva com sucesso!' })
        setEditingRows(prev => {
          const newSet = new Set(prev)
          newSet.delete(darf.id)
          return newSet
        })
      },
      onError: () => {
        toast({ variant: 'destructive', description: 'Houve um erro ao salvar a DARF.' })
      },
    },
  )
}
const handleAddDarfControl = () => {
  const tempId = `temp-${Date.now()}`
  setDarfControls(prev => [
    {
      id: tempId,
      id_apuracao_competencia: id_apuracao_competencia.trim() || undefined,
      anoCompetencia: '',
      mesCompetencia: '',
      value: 0,
      isPago: false,
      status: false,
      isAtivo: true
    },
    ...prev
  ])
  setEditingRows(prev => new Set(prev).add(tempId))

  onChange && onChange()
}

const handleDeleteDarfControl = (darf: Darf) => {
  if (darf.id.startsWith('temp-')) {
    setDarfControls(prevControls => prevControls.filter(control => control.id !== darf.id))
    return
  }

  deleteDarf(darf.id, {
    onSuccess: () => {
      setDarfControls(prevControls => prevControls.filter(control => control.id !== darf.id))
      toast({ description: 'DARF excluída com sucesso!' })
      onChange && onChange()
    },
    onError: () => {
      toast({ variant: 'destructive', description: 'Houve um erro ao excluir a DARF.' })
    }
  })
}

  const isAdmin = user?.typeUser === 'ADMIN'

  return (
    <div className="bg-white rounded-lg shadow-lg mt-6 overflow-hidden mx-auto">
      <div className="bg-gradient-to-r  p-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl text-foreground font-bold">Detalhe de Crédito</h2>
          </div>
          <div className="space-x-3">
            {isAdmin && (
              <>

                <Button
                  onClick={handleAddDarfControl}
                  size="sm"
                  disabled={!isEditable}
                  className="bg-green-500 hover:bg-green-600 text-white"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Adicionar
                </Button>
              </>
            )}
          </div>
          </div>
        </div>
<div className="mt-6 border border-gray-200 rounded-lg overflow-hidden">



  
  <div className="overflow-x-auto">
    <Table className="w-full min-w-[520px]">
      <TableHeader className="bg-gray-50">
        <TableRow>
          <TableHead className="text-foreground font-semibold">Status DARF</TableHead>
          <TableHead className="text-foreground font-semibold">Competência</TableHead>
          <TableHead className="text-foreground font-semibold">Valor</TableHead>
          {isAdmin && <TableHead className="text-foreground font-semibold">Ações</TableHead>}
        </TableRow>
      </TableHeader>

      <TableBody>
        
        {darfControls.length > 0 ? (
          darfControls.map(control => {
            const isEditing = editingRows.has(control.id)

            return (
              <TableRow key={control.id}>
              <TableCell>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              checked={control.isPago}
                              onCheckedChange={checked =>
                                isAdmin && handleDarfControlChange(control.id, 'isPago', !!checked)
                              }
                              disabled={!isAdmin}
                              className="data-[state=checked]:bg-green-500"
                            />
                            <span
                              className={`text-xs px-2 py-1 rounded-full font-medium ${
                                control.isPago ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                              }`}
                            >
                              {control.isPago ? 'Pago' : 'Não Pago'}
                            </span>
                          </div>
                        </TableCell>

                         <TableCell>
                          {isEditing && isAdmin && isEditable ? (
                            <MesAnoInput
                              value={`${control.mesCompetencia || ''}${control.anoCompetencia ? '/' + control.anoCompetencia : ''}`}
                              onChange={(value: string | number | boolean) =>
                                handleDarfControlChange(control.id, 'competencia', value)
                              }
                            />
                          ) : (
                            <div className="bg-input px-3 py-2 rounded text-center font-mono">
                              {control.mesCompetencia || '--'}/{control.anoCompetencia || '----'}
                            </div>
                          )}
                        </TableCell>

                <TableCell>
                  {isEditing && isAdmin && isEditable ? (
                    <MaskInputDin
                      value={Number(control.value) || 0}
                      onChange={value => handleDarfControlChange(control.id, 'value', value)}
                    />
                  ) : (
                    <div className="px-3 py-2 bg-input rounded text-center">
                      {formatCurrency(control.value ?? 0)}
                    </div>
                  )}
                </TableCell>

                {isAdmin && (
                  <TableCell>
                    {isEditing ? (
                      <Button
                        onClick={() => handleSaveDarfControl(control)}
                        size="sm"
                        disabled={!isEditable}
                        className="bg-green-500 hover:bg-green-600 text-white"
                      >
                        <Save className="h-3 w-3" />
                      </Button>
                    ) : (
                      <Button
                        onClick={() => handleToggleEdit(control.id)}
                        size="sm"
                        variant="outline"
                        disabled={!isEditable}
                      >
                        {isEditable ? <Edit2 className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
                      </Button>
                    )}
                    <Button
                      onClick={() => handleDeleteDarfControl(control)}
                      size="sm"
                      variant="outline"
                      disabled={!isAdmin || !isEditable}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            )
          })
        ) : (
          <TableRow>
            <TableCell colSpan={4} className="text-center text-muted-foreground py-6">
              Nenhum DARF encontrado.
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </div>
</div>
      {/* Modal de Anexo */}
      {isModalOpen.open && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-lg w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">Anexar Arquivo</h2>
            </div>
            <FileManager
              id={isModalOpen.id || ''}
              type="credit"
              id_client={cnpj}
              id_apuracao={id_apuracao ?? ''}
              id_product={id_product ?? ''}
              id_apuracao_competencia={id_apuracao_competencia}
              onClose={() => setIsModalOpen({ open: false, id: null })}
            />
          </div>
        </div>
      )}
    </div>
  )
}
